import React from 'react';
import { __, __n } from 'i18n/index';

const PHASE_LABELS = {
	rps: () => __('Rock paper scissors'),
	stages: () => __('Picking stages'),
	characters: () => __('Picking characters'),
	playing: () => __('Playing'),
};

export function LiveMatchesPanel({ matches, gamesById, siteUrl }) {
	return (
		<section className='hub-panel hub-panel--live' aria-labelledby='hub-live-heading'>
			<h2 id='hub-live-heading' className='hub-panel__heading'>
				<span className='hub-panel__live-dot' aria-hidden='true' />
				{__('Live matches')}
			</h2>
			{matches.length === 0
				? <p className='hub-panel__empty'>{__('No matches are being played right now.')}</p>
				: (
					<ul className='hub-panel__list'>
						{matches.map((match) => (
							<li key={match.match_id}>
								<a className='hub-panel__row hub-live-match' href={siteUrl + match.path}>
									<span className='hub-live-match__players'>
										<span>{teamNames(match.teams[0])}</span>
										{match.score
											? <span className='hub-live-match__score'>{match.score[0]} - {match.score[1]}</span>
											: <span className='hub-live-match__score hub-live-match__score--hidden'>{__('vs')}</span>}
										<span>{teamNames(match.teams[1])}</span>
									</span>
									<span className='hub-panel__muted hub-live-match__status'>
										{liveMatchStatus(match)}
										{match.stage ? <span className='hub-live-match__stage'>{' · ' + match.stage.name}</span> : null}
										{(match.characters || []).map((pick) => (
											<img key={pick.player_id} className='hub-live-match__character' src={pick.image_url} alt={pick.name} title={pick.name} />
										))}
									</span>
									<span className='hub-panel__game'>{gamesById[match.ladder_id] ? gamesById[match.ladder_id].name : ''}</span>
									<span className={'hub-panel__badge' + (match.ranked ? ' hub-panel__badge--ranked' : '')}>
										{match.ranked ? __('Ranked') : __('Friendlies')}
									</span>
								</a>
							</li>
						))}
					</ul>
				)}
		</section>
	);
}

function teamNames(team) {
	return team.map((player) => player.username).join(' & ');
}

function liveMatchStatus(match) {
	const phase = PHASE_LABELS[match.phase] ? PHASE_LABELS[match.phase]() : null;
	if(match.game_number < 1) {
		return phase || __('Getting ready');
	}
	const game = __('Game %s', match.game_number);
	return phase ? game + ' · ' + phase : game;
}

export function SearchersPanel({ searchers, gamesById, siteUrl }) {
	return (
		<section className='hub-panel' aria-labelledby='hub-searchers-heading'>
			<h2 id='hub-searchers-heading' className='hub-panel__heading'>{__('Searching right now')}</h2>
			{searchers.length === 0
				? <p className='hub-panel__empty'>{__('Nobody is searching right now. Be the first.')}</p>
				: (
					<ul className='hub-panel__list'>
						{searchers.map((searcher) => (
							<li key={searcher.player_id + '-' + searcher.ladder_id} className='hub-panel__row'>
								<a href={siteUrl + '/player/' + searcher.player_id}>{searcher.username}</a>
								<span className='hub-panel__game'>{gamesById[searcher.ladder_id] ? gamesById[searcher.ladder_id].name : ''}</span>
								<span className={'hub-panel__badge' + (searcher.ranked ? ' hub-panel__badge--ranked' : '')}>
									{searcher.ranked ? __('Ranked') : __('Friendlies')}
								</span>
							</li>
						))}
					</ul>
				)}
		</section>
	);
}

/**
 * Results, friendlies and searches that ended without a match, newest first
 * (services\intro\BroadcastHubService::recentMatches).
 */
export function RecentMatchesPanel({ matches, gamesById, siteUrl, now = Date.now() }) {
	return (
		<section className='hub-panel' aria-labelledby='hub-recent-heading'>
			<h2 id='hub-recent-heading' className='hub-panel__heading'>{__('Recent matches')}</h2>
			{matches.length === 0
				? <p className='hub-panel__empty'>{__('No matches yet. Yours could be the first.')}</p>
				: (
					<ul className='hub-panel__list'>
						{matches.map((recent) => (
							<li key={recent.kind + '-' + (recent.match_id || recent.search_id)} className='hub-panel__row hub-panel__row--result'>
								<ResultGameIcon game={gamesById[recent.ladder_id]} />
								<RecentMatchSummary recent={recent} siteUrl={siteUrl} />
								<RankedBadge ranked={recent.kind !== 'friendly' && recent.ranked} />
								<span className='hub-panel__time'>{timeAgo(recent.at, now)}</span>
							</li>
						))}
					</ul>
				)}
		</section>
	);
}

function RecentMatchSummary({ recent, siteUrl }) {
	if(recent.kind === 'search') {
		return (
			<span className='hub-panel__players'>
				<a className='hub-panel__name' href={siteUrl + '/player/' + recent.player_id}>{recent.username}</a>
				<span className='hub-panel__detail'>{__('No opponent found')}</span>
			</span>
		);
	}
	if(recent.kind === 'friendly') {
		return (
			<a className='hub-panel__players hub-panel__match-link' href={siteUrl + recent.path}>
				<span className='hub-panel__pairing'>
					<span className='hub-panel__name'>{recent.players[0]}</span>
					<span className='hub-panel__versus'>{__('vs')}</span>
					<span className='hub-panel__name'>{recent.players[1]}</span>
				</span>
				<FriendlyDetail friendly={recent} />
			</a>
		);
	}
	return (
		<a className='hub-panel__players hub-panel__match-link' href={siteUrl + recent.path}>
			<span className='hub-panel__winner'>
				<CrownIcon />
				<span className='hub-panel__name'>{recent.winner}</span>
			</span>
			<span className='hub-panel__loser'>
				<span className='hub-panel__name'>{recent.loser}</span>
			</span>
		</a>
	);
}

function FriendlyDetail({ friendly }) {
	if(friendly.in_progress) {
		return <span className='hub-panel__detail hub-panel__detail--playing'>{__('Playing')}</span>;
	}
	if(friendly.games < 1) {
		return null;
	}
	return (
		<span className='hub-panel__detail'>
			{__n('%s game', '%s games', friendly.games, friendly.games)}
		</span>
	);
}

function RankedBadge({ ranked }) {
	return (
		<span className={'hub-panel__badge' + (ranked ? ' hub-panel__badge--ranked' : '')}>
			{ranked ? __('Ranked') : __('Friendlies')}
		</span>
	);
}

function ResultGameIcon({ game }) {
	if(!game) {
		return <span className='hub-panel__game-icon' />;
	}
	return <img className='hub-panel__game-icon' src={game.icon_url} alt={game.name} title={game.name} />;
}

function CrownIcon() {
	return (
		<svg className='hub-panel__crown' viewBox='0 0 24 24' role='img' aria-label={__('Winner')} focusable='false'>
			<path fill='currentColor' d='M3 7l4.5 4L12 4l4.5 7L21 7l-2 11H5L3 7z' />
		</svg>
	);
}

export function HowItWorks() {
	const steps = [
		{ title: __('Pick a game'), text: __('Melee, Ultimate, Project+, 64 or Brawl. Friendlies or ranked.') },
		{ title: __('Hit Find a match'), text: __('We pair you with someone nearby. No chat room needed.') },
		{ title: __('Play and climb'), text: __('Report the result. Ranked sets move you up the ladder.') },
	];
	return (
		<section className='hub-how' aria-labelledby='hub-how-heading'>
			<h2 id='hub-how-heading' className='hub-how__heading'>{__('How it works')}</h2>
			<ol className='hub-how__steps'>
				{steps.map((step) => (
					<li key={step.title} className='hub-how__step'>
						<h3>{step.title}</h3>
						<p>{step.text}</p>
					</li>
				))}
			</ol>
		</section>
	);
}

function timeAgo(atSeconds, now) {
	const minutes = Math.max(0, Math.floor((now / 1000 - atSeconds) / 60));
	if(minutes < 1) {
		return __('just now');
	}
	if(minutes < 60) {
		return __('%sm ago', minutes);
	}
	const hours = Math.floor(minutes / 60);
	if(hours < 24) {
		return __('%sh ago', hours);
	}
	return __('%sd ago', Math.floor(hours / 24));
}
